import api from './api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'packages';
import { selectShipment, selectItemWithoutShipment } from './selector';
import {
    resolveMostPreciseTranslationKey,
    getDefaultErrorKey,
    isUnSupportedProduct
} from 'tradera-helpers/shipping-validation-errors';

export const deleteShipment = createAsyncThunk(
    'shippingCheckout/deleteShipment',
    async ({ shipmentId, isChangingProvider }, thunkAPI) => {
        try {
            await api.deleteShipment(shipmentId);
            return { shipmentId, isChangingProvider };
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const createShipmentCheckout = createAsyncThunk(
    'shippingCheckout/createShipmentCheckout',
    async (_, thunkAPI) => {
        try {
            const result = await api.createShipmentCheckout();
            return result.data;
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getShippingOptions = createAsyncThunk(
    'shippingCheckout/getShippingOptions',
    async (
        {
            fromCountry,
            toCountry,
            bringEnabled,
            postnordEnabled,
            dhlEnabled,
            schenkerEnabled
        },
        thunkAPI
    ) => {
        try {
            const result = await api.getShippingOptions(fromCountry, toCountry);

            return {
                data: result.data,
                bringEnabled: bringEnabled,
                postnordEnabled: postnordEnabled,
                dhlEnabled: dhlEnabled,
                schenkerEnabled: schenkerEnabled
            };
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const updateShipment = createAsyncThunk(
    'shippingCheckout/updateShipment',
    async (shipmentDetails, thunkAPI) => {
        try {
            const { data } = await api.setShipmentDetails(shipmentDetails);
            const { errors } = data.responseStatus;
            if (errors) {
                const mostPreciseTranslationKeys = resolveMostPreciseTranslationKey(
                    errors
                );
                return thunkAPI.rejectWithValue({
                    mostPreciseErrorKey: mostPreciseTranslationKeys
                });
            }
            thunkAPI.dispatch(createShipmentCheckout());
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue({
                mostPreciseErrorKey: getDefaultErrorKey()
            });
        }
    }
);

export const updateShippingProvider = (id, payload) => (dispatch, getState) => {
    const shipment = selectShipment(getState(), id);

    const { providerId, id: productId } = payload;
    const { purchaseOrderId, shipmentId, receiver } = shipment;

    const shippingPayload = buildShippingPayload(
        shipmentId,
        providerId,
        productId,
        purchaseOrderId,
        receiver
    );

    dispatch(updateShipment(shippingPayload));
};

export const updateShippingAddress = (id, payload) => (dispatch, getState) => {
    const shipment = selectShipment(getState(), id);

    const {
        purchaseOrderId,
        shippingProvider,
        shipmentId,
        product,
        receiver
    } = shipment;

    const address = {
        name: payload.name || receiver.name,
        city: payload.city || receiver.city,
        zipCode: payload.zipCode || receiver.zipCode,
        street: payload.streetName || receiver.street
    };

    const shippingPayload = buildShippingPayload(
        shipmentId,
        shippingProvider,
        product.id,
        purchaseOrderId,
        address
    );
    dispatch(updateShipment(shippingPayload));
};

export const createShipment = createAsyncThunk(
    'shippingCheckout/createShipment',
    async (payload, thunkAPI) => {
        const {
            item: { selectedShipping },
            buyerContactDetails
        } = selectItemWithoutShipment(thunkAPI.getState());

        const { address } = payload;
        const shippingPayload = buildShippingPayload(
            null,
            payload.providerId || selectedShipping.providerId,
            payload.productId || selectedShipping.productId,
            payload.purchaseOrderId,
            {
                name: address?.name || buyerContactDetails.fullName,
                city: address?.city || buyerContactDetails.town,
                zipCode: address?.zipCode || buyerContactDetails.zipCode,
                street: address?.street || buyerContactDetails.streetName
            }
        );

        try {
            const { data } = await api.setShipmentDetails(shippingPayload);
            const { errors } = data.responseStatus;
            if (errors) {
                // Can happen i combined shipping when seller sets custom shipping price and changes product
                // in that case we want to let the seller choose a product from shipping selector
                if (isUnSupportedProduct(errors[0])) {
                    return thunkAPI.rejectWithValue({
                        hasUnSupportedProduct: true
                    });
                }

                const mostPreciseTranslationKey = resolveMostPreciseTranslationKey(
                    errors
                );

                return thunkAPI.rejectWithValue({
                    mostPreciseErrorKey: mostPreciseTranslationKey
                });
            }
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue({
                mostPreciseErrorKey: getDefaultErrorKey()
            });
        }
    }
);

export const markAsBooked = createAsyncThunk(
    'shippingCheckout/markAsBooked',
    async (purchaseOrderId, thunkAPI) => {
        try {
            return await api.setShippingAsBooked(purchaseOrderId);
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const markAsShipped = createAsyncThunk(
    'shippingCheckout/markAsShipped',
    async (purchaseOrderId, thunkAPI) => {
        try {
            return await api.setShippingAsShipped(purchaseOrderId);
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getItemWithoutShipment = createAsyncThunk(
    'selectShipment/getItemWithoutShipment',
    async (purchaseOrderId, thunkAPI) => {
        try {
            const { data } = await api.getMemberSoldItemShippingInfo(
                purchaseOrderId
            );
            const { responseStatus } = data;
            if (responseStatus?.errorCode) {
                return thunkAPI.rejectWithValue({
                    errorCode: responseStatus?.errorCode
                });
            }

            return data;
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

const buildShippingPayload = (
    shipmentId,
    shippingProvider,
    shippingProductId,
    purchaseOrderId,
    receiver
) => ({
    shipmentId, // nullable if `null` -> new shipment is created
    shippingProvider,
    buyerAddress: {
        name: receiver.name,
        city: receiver.city,
        zipCode: receiver.zipCode,
        streetName: receiver.street
    },
    shippingProductId,
    purchaseOrderId
});
